import type { FieldType } from "@broadpaper/core";

/** What the bytes turned out to be. Sniffed from content, never from the extension. */
export type SourceFormat = "csv" | "tsv" | "xlsx" | "json" | "ndjson" | "unknown";

/**
 * Something the user should be told, in words they can act on.
 *
 * `field` names the column it is about, when it is about one. `action` is the
 * thing the review screen should offer to do about it — the whole point of
 * surfacing a notice rather than logging it.
 */
export interface Notice {
  level: "info" | "ask" | "warn";
  message: string;
  field?: string;
  action?: "confirm-date-order" | "confirm-header-row" | "check-type" | "none";
}

/**
 * One column of one dataset, as BroadPaper understood it.
 *
 * `name` and `label` are two different things and both matter. `name` is the
 * identifier the expression language accepts, which is what a template binds
 * to; `label` is what a person reads, and it keeps the punctuation, the
 * currency symbol and the capitalisation of the original heading. Losing the
 * second is how a well-titled spreadsheet becomes a document full of
 * `total_fees_gbp`.
 */
export interface SourceColumn {
  /** The heading exactly as it appeared in the file. */
  header: string;
  /** An identifier the expression language accepts: `totalFees`. */
  name: string;
  /** What a person reads: `Total Fees (£)`. */
  label: string;
  type: FieldType;
  /** A date pattern, or a number pattern, in the form `FieldSchema.format` takes. */
  format?: string;
  /** ISO 4217, for `currency` columns. */
  currency?: string;
  /** A few distinct non-empty values, for showing the user what the column holds. */
  samples: unknown[];
  /**
   * How much of the column agreed with the winning type, 0–1.
   *
   * Not shown as a number. It decides whether the review screen presents the
   * type as settled or asks about it, which is the only decision it needs to
   * drive.
   */
  confidence: number;
  /** Whether the column is offered to templates at all. The user can turn one off. */
  include: boolean;
  /** Every non-empty value is the same. Such a column is a good candidate for a scalar field. */
  constant: boolean;
  /** 0–1. A mostly-empty column is worth flagging before somebody binds a document to it. */
  blankRatio: number;
}

/** A parsed table: one sheet, one file, or one paste. */
export interface Dataset {
  /** The sheet tab, the file name without its extension, or "Pasted data". */
  label: string;
  columns: SourceColumn[];
  /** Rows, keyed by column `name`, with values already coerced to the column's type. */
  rows: Array<Record<string, unknown>>;
  /** True when the first row of the file was a heading row rather than data. */
  headerRow: boolean;
  notices: Notice[];
}

export interface IngestResult {
  format: SourceFormat;
  /** One per sheet for a workbook; one otherwise. Empty sheets are dropped. */
  datasets: Dataset[];
  notices: Notice[];
}

export interface IngestOptions {
  /**
   * The file's name, used only for a dataset label and as a tie-break when the
   * content is genuinely ambiguous. Never as the decision: a `.csv` full of
   * tabs is a TSV, and a `.txt` pasted out of Excel is one too.
   */
  filename?: string;
  /**
   * Rows to parse. The default is generous enough for the whole of most files
   * and bounded so a forty-megabyte workbook cannot take the tab with it.
   */
  maxRows?: number;
  /** Columns beyond this are dropped with a notice. A document cannot use 900 fields. */
  maxColumns?: number;
  /**
   * Which way round an ambiguous `03/04/2026` is read. Both orders are recorded
   * as a question for the user either way — this only decides the default.
   */
  dayFirst?: boolean;
}

export const DEFAULT_MAX_ROWS = 50_000;
export const DEFAULT_MAX_COLUMNS = 120;
/** Rows the type vote looks at. More than this buys nothing and costs time. */
export const INFERENCE_SAMPLE = 2_000;
